import { omit } from "lodash-es";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { uuidv4 } from "../../../utils/uuid";
import { SupabaseEventService } from "../../event/server/supabase-event-service";
import type { MomentFormSchedule } from "../../moment/types/moment-form-moment";
import { SupabaseScheduleService } from "../server/supabase-schedule-service";
import { isScheduled } from "../types/utils/is-scheduled";

export function useScheduleService(): (schedule: MomentFormSchedule | null) => Promise<void> {
  return useSupabaseCallback(
    async ({ supabase }, schedule: Omit<MomentFormSchedule, "interval"> | null) => {
      console.log("Saving schedule", schedule);

      if (!schedule) return;

      let event_key = schedule?.event?.key || "";

      const isOneOff = !isScheduled(schedule.event);
      if (isOneOff) {
        if (!schedule.event?.date || !schedule?.event?.key) {
          throw new Error("One-off event is invalid.");
        }

        event_key = `date-only-${uuidv4()}`;

        const eventValues = {
          id: uuidv4(),
          key: event_key,
          source: "WEB-DATE",
          repeat: null,
          date: schedule.event?.date,
          number_of_repeats: 0,
        };

        const { data: eventInsertData, error: eventInsertError } = await new SupabaseEventService(
          supabase,
        ).insert(eventValues);

        if (eventInsertError || !eventInsertData) {
          throw new Error(eventInsertError?.message);
        }
      }

      const { data, error } = await new SupabaseScheduleService(supabase).upsert(
        omit(
          {
            ...omit(schedule, "event"),
            event_key,
            // Birthday is a special case where we want to force the event_initial and event_repeat to true
            event_initial: schedule.event?.key === "birthday" ? true : schedule.event_initial,
            event_repeat: schedule.event?.key === "birthday" ? true : schedule.event_repeat,
          },
          "interval",
        ),
      );

      if (error || !data) throw new Error(error?.message);
    },
    [],
  );
}
